
import {
  computed, defineComponent, reactive, watch,
} from 'vue';
import { useSelecionarBase } from '@/core/composables/SelecionarBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoDadosRelatorio from '@/servicos/Relatorios/ServicoDadosRelatorio';

export default defineComponent({
  name: 'SelecionarRelatorioPainelVendas',
  props: {
    codigoSelecionado: {
      type: [Number, String],
      default: undefined,
    },
    descricaoSelecionado: {
      type: String,
      default: '',
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Selecione o Relatório',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    // Mantendo as outras props conforme seu código original
    varios: { type: Boolean, default: false },
    title: { type: String, default: 'Relatório' },
    dataAttributeColuna: { type: String, default: '' },
  },
  emits: ['update:codigoSelecionado', 'update:descricaoSelecionado', 'change'],
  setup(props, { emit }) {
    const {
      selecionarBase,
      montaOpcoesComListaConsultaRapida,
      verificacaoUltimaPesquisa,
      instanciaParametrosConsultaRapidaPorCodigo,
      instanciaParametrosConsultaRapidaPesquisa,
    } = useSelecionarBase(props, emit);

    const servicoDadosRelatorio = new ServicoDadosRelatorio();
    let debounce = 0;

    const state = reactive({
      buscandoDados: false,
    });

    function preencheListaOpcoes(): void {
      if (UtilitarioGeral.validaLista(selecionarBase.listaItensConsultaRapida)) {
        selecionarBase.listaOpcoes = montaOpcoesComListaConsultaRapida(selecionarBase.listaItensConsultaRapida);
      }
    }

    function preencherDescricaoRelatorio(codigo: any) {
      if (!UtilitarioGeral.valorValido(codigo)) return;

      const option = selecionarBase.listaOpcoes.find((c) => Number(c.value) === Number(codigo));
      if (option) {
        emit('update:descricaoSelecionado', option.label);
      }
    }

    async function obterRelatorioPorCodigo(valor: any) {
      const valorBusca = Number(valor);
      if (UtilitarioGeral.valorValido(valorBusca) || valorBusca <= 0) return;
      const existeNaLista = selecionarBase.listaOpcoes.some((c) => Number(c.value) === valorBusca);

      if (!existeNaLista && props.descricaoSelecionado) {
        selecionarBase.listaOpcoes = [{
          value: valorBusca,
          label: props.descricaoSelecionado,
        }];
      }
      if (existeNaLista) {
        preencherDescricaoRelatorio(valorBusca);
        return;
      }
      state.buscandoDados = true;
      try {
        const parametros = instanciaParametrosConsultaRapidaPorCodigo(valorBusca, true);
        const resultado = await servicoDadosRelatorio.consultaRapida(parametros);
        if (resultado && resultado.length > 0) {
          selecionarBase.listaItensConsultaRapida = resultado;
          preencheListaOpcoes();
          preencherDescricaoRelatorio(valorBusca);
        }
      } catch (error) {
        console.error('Erro ao buscar relatório:', error);
      } finally {
        state.buscandoDados = false;
      }
    }

    async function pesquisarRelatorio(valorPesquisa: any) {
      selecionarBase.emDigitacao = true;
      if (!verificacaoUltimaPesquisa(valorPesquisa)) return;

      state.buscandoDados = true;
      clearTimeout(debounce);

      debounce = setTimeout(async () => {
        try {
          const parametros = instanciaParametrosConsultaRapidaPesquisa(valorPesquisa, true);
          const resultado = await servicoDadosRelatorio.consultaRapida(parametros);

          selecionarBase.listaItensConsultaRapida = resultado;
          preencheListaOpcoes();
          selecionarBase.valorUltimaPesquisa = valorPesquisa;
        } finally {
          state.buscandoDados = false;
        }
      }, 600);
    }

    const computedCodigoSelecionado = computed({
      get: () => (UtilitarioGeral.validaNumerico(props.codigoSelecionado)
        ? Number(props.codigoSelecionado)
        : undefined),
      set: (val) => {
        emit('update:codigoSelecionado', val);
      },
    });
    watch(
      () => props.codigoSelecionado,
      (codigo) => {
        if (!UtilitarioGeral.valorValido(codigo)) return;

        const existe = selecionarBase.listaOpcoes.some(
          (o) => Number(o.value) === Number(codigo),
        );

        if (!existe && props.descricaoSelecionado) {
          selecionarBase.listaOpcoes.push({
            value: Number(codigo),
            label: props.descricaoSelecionado,
          });
        }

        obterRelatorioPorCodigo(codigo);
      },
      { immediate: true },
    );

    function change(val: any) {
      emit('change', val);
    }

    return {
      props,
      state,
      selecionarBase,
      computedCodigoSelecionado,
      pesquisarRelatorio,
      change,
    };
  },
});
